import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaChartLine, FaTruck, FaGasPump, FaClock, FaCheckCircle,
  FaTimes, FaExclamationTriangle, FaDownload, FaEye,
  FaFilter, FaSearch, FaCalendarAlt, FaMoneyBillWave,
  FaRoad, FaUser, FaWeightHanging, FaMapMarkerAlt,
  FaFileAlt, FaClipboardList, FaArrowRight, FaSpinner,
  FaFileExcel, FaFilePdf
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';
import ExportModal from '../components/ExportModal';

function SubbieReports({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showExportModal, setShowExportModal] = useState(false);
  const [reports, setReports] = useState({
    truckPerformance: [],
    fuelEfficiency: [],
    driverHours: [],
    revenue: [],
    loads: [],
    truckSweating: []
  });
  const [summary, setSummary] = useState({
    totalLoads: 0,
    completedLoads: 0,
    totalTonnage: 0,
    totalRevenue: 0,
    avgFuelEfficiency: 0,
    avgTurnaround: 0,
    onTimeDelivery: 0,
    emptyRunning: 0
  });
  const [subbieId, setSubbieId] = useState(null);
  const [user, setUser] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, []);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchReports(savedId);
        return;
      }

      const response = await fetch(`/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const id = data.subcontractors[0].id;
        setSubbieId(id);
        localStorage.setItem('subbieId', id);
        fetchReports(id);
      } else {
        toast.error('No subcontractor profile found');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchReports = async (id) => {
    setLoading(true);
    try {
      const reqRes = await fetch(`/subbie/all-requirements/${id}`);
      const reqData = await reqRes.json();
      
      if (reqData.success) {
        const requirements = reqData.requirements || [];
        processReportData(requirements, id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      toast.error('Failed to load reports');
      setLoading(false);
    }
  };

  const processReportData = async (requirements, id) => {
    try {
      const fleetRes = await fetch(`/subbie/fleet/${id}`);
      const fleetData = await fleetRes.json();
      const trucks = fleetData.success ? fleetData.trucks || [] : [];
      const drivers = fleetData.success ? fleetData.drivers || [] : [];

      const uploadsRes = await fetch(`/subbie/uploads/${id}`);
      const uploadsData = await uploadsRes.json();
      const uploads = uploadsData.success ? uploadsData.uploads || [] : [];

      const hoursRes = await fetch(`/subbie/hours/${id}`);
      const hoursData = await hoursRes.json();
      const driverHoursList = hoursData.success ? hoursData.hours || [] : [];

      const loads = requirements
        .filter(r => r.jobs)
        .map(r => ({
          id: r.id,
          load_number: r.jobs?.job_number || `LOAD-${r.id}`,
          truck_id: r.truck_id || null,
          weight_mt: r.jobs?.total_quantity_mt || 0,
          rate: (r.jobs?.subbie_target_price || 0) * (r.jobs?.total_quantity_mt || 0),
          rate_per_mt: r.jobs?.subbie_target_price || 0,
          status: r.status === 'completed' || r.status === 'closed' ? 'completed' : 'active',
          distance_km: r.jobs?.distance_km || 0,
          origin: r.jobs?.origin || '',
          destination: r.jobs?.destination || '',
          actual_delivery_date: r.updated_at || null,
          estimated_delivery_date: r.jobs?.estimated_end_date || null
        }));

      const fuelLogs = uploads
        .filter(u => u.upload_type === 'fuel')
        .map(u => ({
          id: u.id,
          truck_id: u.truck_id || null,
          litres: parseFloat(u.fuel_liters) || 0,
          total_cost: parseFloat(u.fuel_total_cost) || 0,
          odometer: parseFloat(u.fuel_odometer) || 0,
          fuel_date: u.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
          truck: trucks.find(t => t.id === u.truck_id)?.registration_number || 'Unknown'
        }));

      const truckPerformance = trucks.map(truck => {
        const truckLoads = loads.filter(l => l.truck_id === truck.id);
        const totalLoads = truckLoads.length;
        const completedLoads = truckLoads.filter(l => l.status === 'completed').length;
        const totalWeight = truckLoads.reduce((sum, l) => sum + (l.weight_mt || 0), 0);
        const truckFuel = fuelLogs.filter(f => f.truck_id === truck.id);
        const totalFuel = truckFuel.reduce((sum, f) => sum + (f.litres || 0), 0);
        const totalDistance = truckLoads.reduce((sum, l) => sum + (l.distance_km || 0), 0);
        const fuelEfficiency = totalDistance > 0 ? totalFuel / totalDistance : 0;
        const revenue = truckLoads.reduce((sum, l) => sum + (l.rate || 0), 0);

        return {
          registration: truck.registration_number,
          totalLoads,
          completedLoads,
          totalWeight: Math.round(totalWeight * 100) / 100,
          fuelEfficiency: Math.round(fuelEfficiency * 100) / 100,
          revenue: Math.round(revenue * 100) / 100,
          completionRate: totalLoads > 0 ? Math.round((completedLoads / totalLoads) * 100) : 0
        };
      });

      const driverHoursSummary = drivers.map(driver => {
        const driverHours = driverHoursList.filter(h => h.driver_id === driver.id);
        const totalHours = driverHours.reduce((sum, h) => sum + (h.hours_worked || 0), 0);
        const daysWorked = new Set(driverHours.map(h => h.date)).size;
        const avgHoursPerDay = daysWorked > 0 ? totalHours / daysWorked : 0;
        const isExceeded = totalHours > 23;

        return {
          name: driver.full_name,
          totalHours: Math.round(totalHours * 10) / 10,
          daysWorked,
          avgHoursPerDay: Math.round(avgHoursPerDay * 10) / 10,
          status: isExceeded ? 'exceeded' : totalHours > 20 ? 'warning' : 'compliant'
        };
      });

      const truckSweating = loads.map(load => {
        const truck = trucks.find(t => t.id === load.truck_id);
        const driver = drivers.find(d => d.id === load.driver_id);
        const fuel = fuelLogs.filter(f => f.truck_id === load.truck_id);
        const totalFuel = fuel.reduce((sum, f) => sum + (f.litres || 0), 0);

        return {
          truck: truck?.registration_number || 'N/A',
          driver: driver?.full_name || 'N/A',
          loadNumber: load.load_number,
          route: `${load.origin} → ${load.destination}`,
          weightLoaded: load.weight_mt || 0,
          weightOffloaded: load.weight_mt || 0,
          fuelUsed: Math.round(totalFuel * 100) / 100,
          status: load.status
        };
      });

      const totalLoads = loads.length;
      const completedLoads = loads.filter(l => l.status === 'completed').length;
      const totalTonnage = loads.reduce((sum, l) => sum + (l.weight_mt || 0), 0);
      const totalRevenue = loads.reduce((sum, l) => sum + (l.rate || 0), 0);
      
      const totalFuel = fuelLogs.reduce((sum, f) => sum + (f.litres || 0), 0);
      const totalDistance = loads.reduce((sum, l) => sum + (l.distance_km || 0), 0);
      const avgFuelEfficiency = totalDistance > 0 ? totalFuel / totalDistance : 0;

      const onTimeLoads = loads.filter(l => l.actual_delivery_date && l.estimated_delivery_date && 
        new Date(l.actual_delivery_date) <= new Date(l.estimated_delivery_date));
      const onTimeDelivery = totalLoads > 0 ? (onTimeLoads.length / totalLoads) * 100 : 0;

      const emptyRunning = loads.filter(l => l.status === 'empty').length / (totalLoads || 1) * 100;

      setSummary({
        totalLoads,
        completedLoads,
        totalTonnage: Math.round(totalTonnage * 100) / 100,
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        avgFuelEfficiency: Math.round(avgFuelEfficiency * 100) / 100,
        avgTurnaround: 0,
        onTimeDelivery: Math.round(onTimeDelivery * 100) / 100,
        emptyRunning: Math.round(emptyRunning * 100) / 100
      });

      setReports({
        truckPerformance,
        fuelEfficiency: fuelLogs,
        driverHours: driverHoursSummary,
        revenue: loads,
        loads: loads,
        truckSweating: truckSweating
      });

    } catch (err) {
      console.error('Error processing reports:', err);
      toast.error('Failed to process report data');
    } finally {
      setLoading(false);
    }
  };

  const getExportData = () => {
    switch (activeTab) {
      case 'overview':
        return [
          { Metric: 'Total Loads', Value: summary.totalLoads },
          { Metric: 'Completed Loads', Value: summary.completedLoads },
          { Metric: 'Total Revenue', Value: `R${summary.totalRevenue.toLocaleString()}` },
          { Metric: 'Total Tonnage', Value: `${summary.totalTonnage} MT` },
          { Metric: 'Avg Fuel Efficiency', Value: `${summary.avgFuelEfficiency} L/km` },
          { Metric: 'On-Time Delivery', Value: `${summary.onTimeDelivery}%` },
          { Metric: 'Empty Running', Value: `${summary.emptyRunning}%` }
        ];
      case 'trucks':
        return reports.truckPerformance || [];
      case 'fuel':
        return reports.fuelEfficiency || [];
      case 'drivers':
        return reports.driverHours || [];
      case 'revenue':
        return reports.revenue || [];
      case 'sweating':
        return reports.truckSweating || [];
      default:
        return [];
    }
  };

  const getExportHeaders = () => {
    switch (activeTab) {
      case 'overview':
        return ['Metric', 'Value'];
      case 'trucks':
        return ['Truck', 'Loads', 'Completed', 'Tonnage (MT)', 'Fuel Eff (L/km)', 'Revenue (R)', 'Completion %'];
      case 'fuel':
        return ['Date', 'Truck', 'Liters', 'Cost (R)', 'Odometer (km)'];
      case 'drivers':
        return ['Driver', 'Total Hours', 'Days Worked', 'Avg Hours/Day', 'Status'];
      case 'revenue':
        return ['Load #', 'Truck', 'Weight (MT)', 'Rate (R/MT)', 'Revenue (R)', 'Status'];
      case 'sweating':
        return ['Truck', 'Driver', 'Load #', 'Route', 'Weight Loaded (MT)', 'Weight Offloaded (MT)', 'Fuel Used (L)', 'Status'];
      default:
        return [];
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'compliant': 'bg-green-100 text-green-600',
      'warning': 'bg-yellow-100 text-yellow-600',
      'exceeded': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'compliant': '✅ Compliant',
      'warning': '⚠️ Approaching Limit',
      'exceeded': '❌ Exceeded'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="reports" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading reports...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  return (
    <SubbieLayout activeTab="reports" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            <FaChartLine className="inline mr-2" style={{ color: '#00A86B' }} />
            Reports & Analytics
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            View your performance metrics and KPIs
          </p>
        </div>
        <button
          onClick={() => setShowExportModal(true)}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:border-green hover:text-green transition-colors"
          style={{ borderColor: '#E8ECEF' }}
        >
          <FaDownload size={14} /> Export
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaClipboardList className="text-blue-600" size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ color: '#0A1929' }}>{summary.totalLoads}</div>
              <div className="text-xs text-gray-500">Total Loads</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FaCheckCircle className="text-green-600" size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ color: '#0A1929' }}>{summary.completedLoads}</div>
              <div className="text-xs text-gray-500">Completed</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FaMoneyBillWave className="text-purple-600" size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ color: '#0A1929' }}>R{summary.totalRevenue.toLocaleString()}</div>
              <div className="text-xs text-gray-500">Total Revenue</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <FaGasPump className="text-orange-600" size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ color: '#0A1929' }}>{summary.avgFuelEfficiency} L/km</div>
              <div className="text-xs text-gray-500">Avg Fuel Efficiency</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'overview' ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-navy'}`}
        >
          <FaChartLine className="inline mr-2" /> Overview
        </button>
        <button
          onClick={() => setActiveTab('trucks')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'trucks' ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-navy'}`}
        >
          <FaTruck className="inline mr-2" /> Truck Performance
        </button>
        <button
          onClick={() => setActiveTab('fuel')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'fuel' ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-navy'}`}
        >
          <FaGasPump className="inline mr-2" /> Fuel Efficiency
        </button>
        <button
          onClick={() => setActiveTab('drivers')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'drivers' ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-navy'}`}
        >
          <FaUser className="inline mr-2" /> Driver Hours
        </button>
        <button
          onClick={() => setActiveTab('revenue')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'revenue' ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-navy'}`}
        >
          <FaMoneyBillWave className="inline mr-2" /> Revenue
        </button>
        <button
          onClick={() => setActiveTab('sweating')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'sweating' ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-navy'}`}
        >
          <FaTruck className="inline mr-2" /> Truck Sweating
        </button>
      </div>

      {/* Tab Content - Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-xs text-gray-500">On-Time Delivery</p>
              <p className="text-2xl font-bold" style={{ color: summary.onTimeDelivery >= 90 ? '#00A86B' : '#F59E0B' }}>
                {summary.onTimeDelivery}%
              </p>
            </div>
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-xs text-gray-500">Empty Running</p>
              <p className="text-2xl font-bold" style={{ color: summary.emptyRunning < 20 ? '#00A86B' : '#EF4444' }}>
                {summary.emptyRunning}%
              </p>
            </div>
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-xs text-gray-500">Avg Turnaround</p>
              <p className="text-2xl font-bold" style={{ color: '#0A1929' }}>N/A</p>
            </div>
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-xs text-gray-500">Total Tonnage</p>
              <p className="text-2xl font-bold" style={{ color: '#0A1929' }}>{summary.totalTonnage} MT</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h3 className="font-bold mb-4" style={{ color: '#0A1929' }}>📊 Performance Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between py-2 border-b" style={{ borderColor: '#E8ECEF' }}>
                <span className="text-gray-500">Total Loads</span>
                <span className="font-medium">{summary.totalLoads}</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: '#E8ECEF' }}>
                <span className="text-gray-500">Completed Loads</span>
                <span className="font-medium">{summary.completedLoads}</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: '#E8ECEF' }}>
                <span className="text-gray-500">Completion Rate</span>
                <span className="font-medium">{summary.totalLoads > 0 ? Math.round((summary.completedLoads / summary.totalLoads) * 100) : 0}%</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: '#E8ECEF' }}>
                <span className="text-gray-500">Total Revenue</span>
                <span className="font-medium">R{summary.totalRevenue.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Truck Performance */}
      {activeTab === 'trucks' && (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Truck</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Loads</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Completed</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tonnage</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Fuel Eff.</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Complete %</th>
                </tr>
              </thead>
              <tbody>
                {reports.truckPerformance.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">No truck data available</td>
                  </tr>
                ) : (
                  reports.truckPerformance.map((truck, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-navy">{truck.registration}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{truck.totalLoads}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{truck.completedLoads}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{truck.totalWeight} MT</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{truck.fuelEfficiency} L/km</td>
                      <td className="px-6 py-3 text-sm text-gray-600">R{truck.revenue.toLocaleString()}</td>
                      <td className="px-6 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${truck.completionRate >= 90 ? 'bg-green-100 text-green-600' : truck.completionRate >= 70 ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'}`}>
                          {truck.completionRate}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fuel Efficiency */}
      {activeTab === 'fuel' && (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Truck</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Liters</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cost</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Odometer</th>
                </tr>
              </thead>
              <tbody>
                {reports.fuelEfficiency.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">No fuel data available</td>
                  </tr>
                ) : (
                  reports.fuelEfficiency.map((fuel, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-600">{fuel.fuel_date || 'N/A'}</td>
                      <td className="px-6 py-3 text-sm font-medium text-navy">{fuel.truck}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{fuel.litres} L</td>
                      <td className="px-6 py-3 text-sm text-gray-600">R{fuel.total_cost?.toFixed(2) || '0.00'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{fuel.odometer || 'N/A'} km</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Driver Hours */}
      {activeTab === 'drivers' && (
        <div className="space-y-4">
          {reports.driverHours.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
              <div className="text-6xl mb-4">👤</div>
              <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Driver Data</h3>
              <p className="text-sm" style={{ color: '#4A5568' }}>Start logging driver hours to see reports.</p>
            </div>
          ) : (
            reports.driverHours.map((driver, index) => (
              <div key={index} className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="font-bold" style={{ color: '#0A1929' }}>{driver.name}</h4>
                      <span className={getStatusBadge(driver.status)}>
                        {getStatusLabel(driver.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm mt-2" style={{ color: '#4A5568' }}>
                      <span>Total: <strong>{driver.totalHours}h</strong></span>
                      <span>Days: <strong>{driver.daysWorked}</strong></span>
                      <span>Avg: <strong>{driver.avgHoursPerDay}h/day</strong></span>
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden w-full max-w-md">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${driver.status === 'exceeded' ? 'bg-red-500' : driver.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min((driver.totalHours / 23) * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {driver.totalHours}h / 23h limit
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Revenue */}
      {activeTab === 'revenue' && (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Load #</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Truck</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Weight</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Rate</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {reports.revenue.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">No revenue data available</td>
                  </tr>
                ) : (
                  reports.revenue.map((load, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-navy">{load.load_number || `LOAD-${index + 1}`}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{load.truck || 'N/A'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{load.weight_mt || 0} MT</td>
                      <td className="px-6 py-3 text-sm text-gray-600">R{load.rate_per_mt || 0}/MT</td>
                      <td className="px-6 py-3 text-sm font-medium text-green-600">R{(load.rate || 0).toLocaleString()}</td>
                      <td className="px-6 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${load.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                          {load.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Truck Sweating */}
      {activeTab === 'sweating' && (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Truck</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Driver</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Load #</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Weight Loaded</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Weight Offloaded</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Fuel Used</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {reports.truckSweating.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">No truck sweating data available</td>
                  </tr>
                ) : (
                  reports.truckSweating.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-navy">{item.truck}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{item.driver}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{item.loadNumber}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{item.route}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{item.weightLoaded} MT</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{item.weightOffloaded} MT</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{item.fuelUsed} L</td>
                      <td className="px-6 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Report"
        data={getExportData()}
        headers={getExportHeaders()}
        filename={`subbie_report_${activeTab}`}
      />
    </SubbieLayout>
  );
}

export default SubbieReports;
